import {
  FormattedMessage,
  injectIntl,
  type intlFormatPropTypes,
} from "react-intl";

import { InputField } from "@ps-design-system/components/Input";
import { Text } from "@ps-design-system/components/Text";

import { FieldLabelWithTooltip } from "../../../AddressForm";
import { getTaxDetailsFormPath } from "../../TaxDetails/TaxDetails.utils";
import { TAX_DETAILS_MAX_TAX_RATE } from "../TaxDetailsFormSection.consts";
import { TAX_DETAILS_FORM_MESSAGES } from "../TaxDetailsFormSection.messages";
import { isRateFieldVisible } from "../TaxDetailsFormSection.utils";

import { useTaxDetailsForm } from "./TaxDetailsFormContext";

type TaxDetailsFormFieldsProps = { intl: intlFormatPropTypes };

/** Business name, tax ID and — for a host — the rate they charge. */
const TaxDetailsFormFieldsComponent = ({ intl }: TaxDetailsFormFieldsProps) => {
  const {
    hasScopedTax,
    isEdit,
    isHost,
    isRegionRequired,
    regionLabel,
    regionName,
    taxTypeName,
  } = useTaxDetailsForm();

  const heading = isEdit
    ? TAX_DETAILS_FORM_MESSAGES.updateHeading
    : TAX_DETAILS_FORM_MESSAGES.addHeading;

  // Checked before the absence below: a country whose province is still unset
  // looks identical here to one levying no such tax, but nothing is missing —
  // the guest simply has another field to fill in first.
  if (isRegionRequired) {
    return (
      <Text typeface="body-2-m">
        <FormattedMessage
          {...TAX_DETAILS_FORM_MESSAGES.selectRegionForTaxDetails}
          values={{ regionLabel, taxType: taxTypeName }}
        />
      </Text>
    );
  }

  // Nothing to register here, so ask for nothing and point at the form that does
  // have something to collect. Better than a field validating against no format.
  if (!hasScopedTax) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-16">
        <Text typeface="body-2-m">
          <FormattedMessage
            {...TAX_DETAILS_FORM_MESSAGES.noRegionalRegistration}
            values={{ region: regionName }}
          />
        </Text>

        <Text
          element="a"
          href={getTaxDetailsFormPath({ isSubregion: false })}
          typeface="body-2-underline"
        >
          <FormattedMessage
            {...TAX_DETAILS_FORM_MESSAGES.goToFederalForm}
            values={{ taxType: taxTypeName }}
          />
        </Text>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-24">
      <Text element="h2" typeface="headline-2-m">
        <FormattedMessage {...heading} values={{ taxType: taxTypeName }} />
      </Text>

      <InputField
        fullWidth={true}
        label={
          <FormattedMessage {...TAX_DETAILS_FORM_MESSAGES.businessNameLabel} />
        }
        name="businessName"
        required={true}
        subcopy={
          <FormattedMessage
            {...TAX_DETAILS_FORM_MESSAGES.businessNameSubcopy}
            values={{ taxType: taxTypeName }}
          />
        }
        testId="tax-details-business-name"
      />

      <InputField
        fullWidth={true}
        label={
          <FieldLabelWithTooltip
            body={
              <FormattedMessage
                {...TAX_DETAILS_FORM_MESSAGES.taxIdTooltipBody}
              />
            }
            heading={
              <FormattedMessage
                {...TAX_DETAILS_FORM_MESSAGES.taxIdTooltipTrigger}
                values={{ taxType: taxTypeName }}
              />
            }
            label={
              <FormattedMessage
                {...TAX_DETAILS_FORM_MESSAGES.taxIdLabel}
                values={{ taxType: taxTypeName }}
              />
            }
            triggerLabel={intl.formatMessage(
              TAX_DETAILS_FORM_MESSAGES.taxIdTooltipTrigger,
              { taxType: taxTypeName },
            )}
          />
        }
        name="taxId"
        required={true}
        subcopy={
          <FormattedMessage
            {...TAX_DETAILS_FORM_MESSAGES.taxIdSubcopy}
            values={{ taxType: taxTypeName }}
          />
        }
        testId="tax-details-tax-id"
      />

      {isRateFieldVisible({ isHost }) && (
        <InputField
          fullWidth={true}
          label={
            <FormattedMessage
              {...TAX_DETAILS_FORM_MESSAGES.taxRateLabel}
              values={{ taxType: taxTypeName }}
            />
          }
          name="taxRate"
          required={true}
          subcopy={
            <FormattedMessage
              {...TAX_DETAILS_FORM_MESSAGES.taxRateSubcopy}
              values={{ maxRate: TAX_DETAILS_MAX_TAX_RATE }}
            />
          }
          testId="tax-details-tax-rate"
        />
      )}
    </div>
  );
};

const TaxDetailsFormFields = injectIntl(TaxDetailsFormFieldsComponent);

export { TaxDetailsFormFields };
